import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../shared/api/client';
import { adaptUser } from '../../shared/api/adapters';
import { registerClerkTokenGetter } from '../../lib/clerkToken';
import { useAppStore } from '../../store/useAppStore';
import type { ApiUser } from '../../shared/types/api';

export function useSessionBootstrap() {
  const { isSignedIn, isLoaded, getToken, signOut } = useAuth();
  const user = useAppStore((s) => s.user);
  const storeSignIn = useAppStore((s) => s.signIn);
  const storeLogout = useAppStore((s) => s.logout);
  const [profileReady, setProfileReady] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const fetchedRef = useRef(false);

  // Register the Clerk token getter so the axios interceptor can use it
  useEffect(() => {
    registerClerkTokenGetter(() => getToken());
  }, [getToken]);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      setProfileReady(true);
      return;
    }

    if (user) {
      setProfileReady(true);
      return;
    }

    if (fetchedRef.current) return;
    fetchedRef.current = true;

    api
      .get<ApiUser>('/api/v1/users/me')
      .then((apiUser) => {
        storeSignIn(adaptUser(apiUser));
        setProfileReady(true);
      })
      .catch(async (err: Error & { status?: number }) => {
        if (err.status === 401 || err.status === 403) {
          // Backend doesn't recognise this Clerk session — force sign-out so
          // RequireAuth redirects cleanly to /login
          try { await signOut(); } catch {}
          storeLogout();
          setProfileReady(true);
        } else {
          // Transient error (network, 5xx) — surface a retry UI
          setBootstrapError(true);
          fetchedRef.current = false;
        }
      });
  }, [isLoaded, isSignedIn, user, storeSignIn, storeLogout, signOut, retryCount]);

  useEffect(() => {
    if (!isSignedIn) fetchedRef.current = false;
  }, [isSignedIn]);

  const retryBootstrap = useCallback(() => {
    setBootstrapError(false);
    fetchedRef.current = false;
    setRetryCount((c) => c + 1);
  }, []);

  return { profileReady, bootstrapError, retryBootstrap };
}

export function useSignOut() {
  const { signOut } = useAuth();
  const storeLogout = useAppStore((s) => s.logout);

  return useCallback(async () => {
    try {
      await signOut();
    } catch {}
    storeLogout();
  }, [signOut, storeLogout]);
}
